import { makeAutoObservable, runInAction } from 'mobx'
import _ from 'lodash'

import { decorate, inject, injectable } from '@evertel/di'
import { Api } from '@evertel/api'
import { SessionService } from '@evertel/session/SessionService'
import { NotificationPermission, ScheduleNotificationOptions, PushImplementation, GPCPushNotification } from './PushImplementation'

import debugModule from 'debug'
const debugLog = debugModule('app:Push:PushService 🔔 ')

// TODO: Reference and import a PushImplementation hat is platform specific

export type PushNotificationEvent = (notification: GPCPushNotification) => void
export type NotificationClickedEvent = (notification: GPCPushNotification) => void

class PushService {

    private onPushNotificationListeners = new Set<PushNotificationEvent>()
    private onNotificationClickedListeners = new Set<NotificationClickedEvent>()
    private initialized = false
    public notificationPermission: NotificationPermission | undefined = undefined

    constructor(
        private api: Api,
        private session: SessionService,
        private pushImplementation: PushImplementation
    ) {
        makeAutoObservable(this)
    }

    async init() {
        if (this.initialized) return

        await this.pushImplementation.init()
        // TODO: get push token and send it to API ?

        const permission = await this.pushImplementation.getCurrentNotificationPermission()
        runInAction(() => {
            this.notificationPermission = permission
        })

        this.pushImplementation.onPermissionChangeCallback = (permission) => {
            runInAction(() => {
                this.notificationPermission = permission
            })

            if (permission === 'granted') {
                this.pushImplementation.showLocalNotification()
            }
        }

        // Make sure any message events are passed to listeners
        this.pushImplementation.onMessageCallback = (notification) => {
            for (const listener of this.onPushNotificationListeners) {
                try {
                    listener(notification)
                } catch (err) { // need to catch to not break background listener
                    console.error('Error in pushImplementation.onMessageCallback', err)
                }
            }
        }

        this.pushImplementation.onNotificationClickedCallback = (notification) => {
            debugLog('onNotificationClickedCallback triggered with notification:', notification)
            debugLog('Number of registered listeners:', this.onNotificationClickedListeners.size)
            for (const listener of this.onNotificationClickedListeners) {
                try {
                    listener(notification)
                } catch (err) { // need to catch to not break background listener
                    console.error('Error in pushImplementation.onNotificationClickedCallback', err)
                }
            }
        }

        runInAction(() => {
            this.initialized = true
        })
    }

    async promptForNotificationPermission() {
        const permission = await this.pushImplementation.promptForNotificationPermission()
        runInAction(() => {
            //don't want to trigger dependency listeners on this every single time it's checked
            if (permission !== this.notificationPermission) {
                this.notificationPermission = permission
            }
        })
        return permission
    }

    get hasPermission() {
        return this.notificationPermission === 'granted'
    }

    async getCurrentNotificationPermission() {
        const permission = await this.pushImplementation.getCurrentNotificationPermission()
        runInAction(() => {
            //don't want to trigger dependency listeners on this every single time it's checked
            if (permission !== this.notificationPermission) {
                this.notificationPermission = permission
            }
        })
        return this.notificationPermission
    }

    async getPushToken(): Promise<string> {
        return this.pushImplementation.getPushToken()
    }

    async deletePushToken() {
        return this.pushImplementation.deletePushToken()
    }

    async postPushToken() {
        const token = await this.pushImplementation.getPushToken()

        if (!token) return

        if (this.session.currentUserId) {
            const result = await this.api.Routes.BlueUser.postPushTokens(this.session.currentUserId, { value: token })
            debugLog('PostPushToken Result:', result)
        }
    }

    async scheduleNotification(options: ScheduleNotificationOptions): Promise<string | undefined> {
        return this.pushImplementation.scheduleNotification(options)
    }

    get pendingNotification() {
        return this.pushImplementation.pendingNotification
    }

    processPendingNotification() {
        debugLog('processPendingNotification()')
        return this.pushImplementation.processPendingNotification()
    }

    async showLocalNotification() {
        return this.pushImplementation.showLocalNotification()
    }

    async setBadge(count: number): Promise<void> {
        return this.pushImplementation.setBadge(count)
    }

    async getDisplayedNotifications(): Promise<GPCPushNotification[]> {
        return this.pushImplementation.getDisplayedNotifications()
    }

    async cancelNotifications(notificationIds: string[]): Promise<boolean> {
        return this.pushImplementation.cancelNotifications(notificationIds)
    }

    addPushNotificationListener(listener: PushNotificationEvent) {
        this.onPushNotificationListeners.add(listener)
    }

    removePushNotificationListener(listener: PushNotificationEvent) {
        this.onPushNotificationListeners.delete(listener)
    }

    addNotificationClickedListener(listener: NotificationClickedEvent) {
        debugLog('addNotificationClickedListener()')
        this.onNotificationClickedListeners.add(listener)
    }

    removeNotificationClickedListener(listener: NotificationClickedEvent) {
        this.onNotificationClickedListeners.delete(listener)
    }
}

decorate(injectable(), PushService)
decorate(inject(Api), PushService, 0)
decorate(inject(SessionService), PushService, 1)
decorate(inject(PushImplementation), PushService, 2)

export { PushService }
